import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchOperationsData } from '../../store/slices/operationsSlice';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Modal } from '../../components/ui/modal';
import TransactionReceipt from '../../components/transactions/TransactionReceipt';
import OperationLayout from '../../components/operations/OperationLayout';
import ProviderCard from '../../components/operations/ProviderCard';
import { PROVIDERS } from '../../lib/constants';
import { Method, operationsService } from '../../services/api/operationsService';
import { addTransaction } from '../../store/slices/transactionsSlice';
import { printService } from '../../services/printService';
import { Transaction } from '../../types';

const STEPS = [
  { label: 'Compañía' },
  { label: 'Número' },
  { label: 'Paquete' },
  { label: 'Confirmar' },
];

const ACCENT = '#06b6d4';

interface PacketItem {
  id: string;
  nombre: string;
  precio: string;
  descripcion?: string;
}

const PaquetesPageSteps: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [availablePackages, setAvailablePackages] = useState<PacketItem[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState<Transaction | null>(null);

  const dispatch = useAppDispatch();
  const { wallet } = useAppSelector((state) => state.wallet as any);
  const { packetMethods } = useAppSelector((state) => state.operations as any);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!fetchedRef.current && (!packetMethods || packetMethods.length === 0)) {
      fetchedRef.current = true;
      dispatch(fetchOperationsData());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
    setSelectedPackage(null);
    setAvailablePackages([]);
    setError(null);
    setCurrentStep(2);
  };

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) setPhoneNumber(value);
  };

  const handlePhoneSubmit = () => {
    if (!phoneNumber || phoneNumber.length < 8) {
      setError('Ingrese un número de teléfono válido');
      return;
    }
    setError(null);
    fetchAvailablePackages();
  };

  const determineTipoServicio = (nombre: string): string => {
    if (nombre.includes('LIBRE+')) return 'Internet ilimitado con mayor velocidad';
    if (nombre.includes('LIBRE')) return 'Internet ilimitado estándar';
    if (nombre.includes('DIA')) return 'Paquete diario de internet';
    return 'Paquete de datos';
  };

  const fetchAvailablePackages = async () => {
    if (!selectedProvider || !phoneNumber) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await operationsService.fetchPackets(phoneNumber, selectedProvider);
      if (!response.success) throw new Error(response.message || 'Error al consultar paquetes');
      const packets = response.data || [];
      setAvailablePackages(packets.map((p: any) => ({
        id: p.id, nombre: p.nombre, precio: p.precio,
        descripcion: p.descripcion || determineTipoServicio(p.nombre),
      })));
      if (packets.length > 0) setCurrentStep(3);
      else setError('No hay paquetes disponibles para este número');
    } catch (err: any) {
      setError(err.message || 'Error al consultar paquetes disponibles');
      setAvailablePackages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePackageSelect = (packageId: string) => {
    setSelectedPackage(packageId);
    setError(null);
    setCurrentStep(4);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!phoneNumber || !selectedProvider || !selectedPackage) {
      setError('Complete todos los campos para continuar');
      return;
    }
    const packageInfo = availablePackages.find(p => p.id === selectedPackage);
    if (!packageInfo) { setError('El paquete seleccionado no es válido'); return; }
    const walletBalance = wallet?.balance ?? 0;
    if (walletBalance < parseFloat(packageInfo.precio)) {
      setError('Saldo insuficiente para comprar este paquete');
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const response = await operationsService.sendPacket(
        phoneNumber, selectedProvider, packageInfo.id, parseFloat(packageInfo.precio), packageInfo.nombre
      );
      if (!response.success) throw new Error(response.message || 'Error al procesar la compra del paquete');
      const transaction = {
        id: response.data?.xid || response.data?.transactionId || `pkg-${Date.now()}`,
        type: 'PAQUETE' as const,
        amount: parseFloat(packageInfo.precio),
        provider: selectedProvider,
        date: new Date().toISOString(),
        status: 'COMPLETED' as const,
        reference: Math.random().toString(36).substring(2, 10).toUpperCase(),
        details: {
          phoneNumber, provider: selectedProvider,
          packageId: packageInfo.id, packageName: packageInfo.nombre,
          price: parseFloat(packageInfo.precio),
        },
      };
      dispatch(addTransaction(transaction));
      setSuccess(`Paquete "${packageInfo.nombre}" activado con éxito al número ${phoneNumber}`);
      setCurrentTransaction(transaction);
      setShowReceiptModal(true);
      setPhoneNumber('');
      setSelectedProvider('');
      setSelectedPackage(null);
      setAvailablePackages([]);
      setCurrentStep(1);
    } catch (err: any) {
      setError(err.message || 'Error al procesar la compra del paquete. Intente nuevamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  const providerList: Method[] =
    packetMethods && packetMethods.length > 0 ? packetMethods : PROVIDERS.PAQUETES;

  const renderStepContent = () => {
    switch (currentStep) {

      case 1:
        return (
          <div className="space-y-5 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Seleccione la compañía</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {providerList.map((p: Method) => (
                <ProviderCard key={p.id} id={p.id} name={p.name} logo={p.logo}
                  onClick={handleProviderSelect} accentColor={ACCENT} />
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Ingrese el número de teléfono</p>
            <div className="max-w-sm mx-auto">
              <Input
                label="Número de teléfono"
                value={phoneNumber} onChange={handlePhoneInputChange}
                placeholder="0000000000" fullWidth size="touch" type="tel"
                leftAddon="+1" useCustomKeyboard keyboardType="numeric"
              />
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" size="lg" onClick={handleBack}>← Atrás</Button>
              <Button size="lg" onClick={handlePhoneSubmit}
                disabled={!phoneNumber || phoneNumber.length < 8} isLoading={isLoading}>
                {isLoading ? 'Consultando...' : 'Consultar Paquetes →'}
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-5 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Seleccione el paquete</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {availablePackages.map((pkg) => (
                <button key={pkg.id} type="button" onClick={() => handlePackageSelect(pkg.id)}
                  className="p-4 rounded-2xl border-2 text-left transition-all duration-150 hover:border-cyan-500 hover:bg-cyan-50 active:scale-95"
                  style={{ borderColor: '#e2e8f0', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <p className="font-bold text-slate-800 truncate">{pkg.nombre}</p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{pkg.descripcion}</p>
                  <p className="mt-2 text-base font-black" style={{ color: ACCENT }}>RD$ {pkg.precio}</p>
                </button>
              ))}
            </div>
            <div className="flex justify-start pt-2">
              <Button variant="outline" size="lg" onClick={handleBack}>← Atrás</Button>
            </div>
          </div>
        );

      case 4: {
        const packageInfo = availablePackages.find(p => p.id === selectedPackage);
        const providerName = packetMethods?.find((p: Method) => p.id === selectedProvider)?.name ||
                             PROVIDERS.PAQUETES.find((p: Method) => p.id === selectedProvider)?.name ||
                             selectedProvider;
        return (
          <div className="space-y-6 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Confirmar compra</p>
            <div className="rounded-2xl border border-slate-100 divide-y divide-slate-100 overflow-hidden"
              style={{ background: '#f8fafc' }}>
              {[
                { label: 'Operador', value: providerName },
                { label: 'Número',   value: `+1 ${phoneNumber}` },
                { label: 'Paquete',  value: packageInfo?.nombre || '' },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center px-5 py-4">
                  <span className="text-sm text-slate-500 font-medium">{label}</span>
                  <span className="text-sm font-bold text-slate-800">{value}</span>
                </div>
              ))}
              <div className="flex justify-between items-center px-5 py-4" style={{ background: `${ACCENT}0d` }}>
                <span className="text-sm text-slate-500 font-medium">Monto</span>
                <span className="text-2xl font-black" style={{ color: ACCENT }}>RD$ {packageInfo?.precio}</span>
              </div>
            </div>
            <Button size="touch" fullWidth isLoading={isProcessing} onClick={handleSubmit}>
              {isProcessing ? 'Procesando...' : '✓ Confirmar Compra'}
            </Button>
            <Button variant="outline" size="lg" onClick={handleBack} disabled={isProcessing}>← Atrás</Button>
          </div>
        );
      }

      default: return null;
    }
  };

  return (
    <>
      <OperationLayout
        title="Paquetes"
        description="Active paquetes de internet, minutos y SMS"
        steps={STEPS} currentStep={currentStep}
        error={error} success={success} accentColor={ACCENT}
        icon={
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"/>
          </svg>
        }
      >
        {renderStepContent()}
      </OperationLayout>

      <Modal isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)}
        title="Resumen de Transacción" size="md">
        {currentTransaction && (
          <TransactionReceipt
            transaction={currentTransaction}
            onClose={() => setShowReceiptModal(false)}
            onPrint={() => { if (currentTransaction) printService.printTicket(currentTransaction); }}
          />
        )}
      </Modal>
    </>
  );
};

export default PaquetesPageSteps;
